/* eslint-disable jsx-a11y/label-has-for */
import React, { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { Form, Message, Button, FormField, Select } from 'semantic-ui-react'
import { toast } from 'react-toastify'

import { SemanticContainer } from './styles'
import api from '../../services/api'
import { useGlobalContext } from '../../contexts/GlobalContext'

function validarComRegex(valor, regex) {
  const regexReal = RegExp(regex)

  if (typeof valor !== 'string') return false
  if (!(regexReal instanceof RegExp)) return false

  return regexReal.test(valor)
}

function validarCelular(numero) {
  if (!numero) return false

  // Remove tudo que não for número
  const apenasNumeros = numero.replace(/\D/g, '')

  // Deve ter 11 dígitos: 2 do DDD + 9 do número
  if (apenasNumeros.length !== 11) return false

  const regex = /^(\d{2})(9\d{8})$/
  return regex.test(apenasNumeros)
}

function formatarCelular(numero) {
  if (!numero) return ''

  // Remove tudo que não for número
  const numeros = numero.replace(/\D/g, '')

  if (numeros.length !== 11) return numero // Retorna original se não tiver 11 dígitos

  const ddd = numeros.slice(0, 2)
  const parte1 = numeros.slice(2, 7)
  const parte2 = numeros.slice(7)

  return `(${ddd}) ${parte1}-${parte2}`
}

function validarCNPJ(cnpj) {
  if (!cnpj) return false

  // Remove tudo que não for número
  const cnpjLimpo = cnpj.replace(/[^\d]/g, '')

  // CNPJ precisa ter 14 dígitos
  if (cnpjLimpo.length !== 14) return false

  // Elimina CNPJs com todos os dígitos iguais
  if (/^(\d)\1{13}$/.test(cnpjLimpo)) return false

  const calcularDigito = (base) => {
    let soma = 0
    let peso = base.length === 12 ? 5 : 6

    for (let i = 0; i < base.length; i++) {
      soma += Number.parseInt(base[i]) * peso--
      if (peso < 2) peso = 9
    }

    const resto = soma % 11
    return resto < 2 ? '0' : String(11 - resto)
  }

  const base = cnpjLimpo.slice(0, 12)
  const digito1 = calcularDigito(base)
  const digito2 = calcularDigito(base + digito1)

  const cnpjCalculado = base + digito1 + digito2

  return cnpjLimpo === cnpjCalculado
}

const formatarCnpj = (cnpj) => {
  const sanitizedCnpj = cnpj.replace(/[^\d]+/g, '')
  if (sanitizedCnpj.length !== 14) {
    return cnpj
  }
  return sanitizedCnpj.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    '$1.$2.$3/$4-$5',
  )
}

const formatarRg = (rg) => {
  const sanitizedRg = rg.replace(/[^\d]+/g, '')
  if (sanitizedRg.length !== 9) {
    return rg
  }
  return sanitizedRg.replace(/(\d{2})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3-$4')
}

function validarRG(rg) {
  if (!rg) return false

  // Remove tudo que não é número ou letra X
  const rgLimpo = rg.replace(/[^\dXx]/g, '').toUpperCase()

  // Verifica se tem exatamente 9 caracteres
  if (!/^\d{8}[0-9X]$/.test(rgLimpo)) {
    return false
  }

  const numeros = rgLimpo.slice(0, 8).split('').map(Number)
  const verificador = rgLimpo[8]

  // Cálculo do dígito verificador
  let soma = 0
  for (let i = 0; i < 8; i++) {
    soma += numeros[i] * (2 + i) // pesos de 2 a 9
  }

  let digito = 11 - (soma % 11)
  if (digito === 10) digito = 'X'
  else if (digito === 11) digito = '0'
  else digito = String(digito)

  return digito === verificador
}

const formatarCpf = (cpf) => {
  const sanitizedCpf = cpf.replace(/[^\d]+/g, '')
  if (sanitizedCpf.length !== 11) {
    return cpf
  }
  return sanitizedCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

const validarCpf = (cpf) => {
  const sanitizedCpf = cpf.replace(/[^\d]+/g, '')
  if (sanitizedCpf.length !== 11 || /^(\d)\1+$/.test(sanitizedCpf)) {
    return false
  }
  let soma = 0
  let resto
  for (let i = 1; i <= 9; i++) {
    soma += Number.parseInt(sanitizedCpf.substring(i - 1, i), 10) * (11 - i)
  }
  resto = (soma * 10) % 11
  if (resto === 10 || resto === 11) {
    resto = 0
  }
  if (resto !== Number.parseInt(sanitizedCpf.substring(9, 10), 10)) {
    return false
  }
  soma = 0
  for (let i = 1; i <= 10; i++) {
    soma += Number.parseInt(sanitizedCpf.substring(i - 1, i), 10) * (12 - i)
  }
  resto = (soma * 10) % 11
  if (resto === 10 || resto === 11) {
    resto = 0
  }
  if (resto !== Number.parseInt(sanitizedCpf.substring(10, 11), 10)) {
    return false
  }
  return true
}

export default function Contato({ location }) {
  const { globalState, globalDispatch } = useGlobalContext()
  console.log(globalState)

  const search = new URLSearchParams(location.search)
  const profile = useSelector((state) => state.user.profile)

  const [did, setDid] = useState(search.get('did') || '')
  const [descricao, setDescricao] = useState('')
  const [fraseologia, setFraseologia] = useState('')
  const [contactFields, setContactFields] = useState([])

  const getContactFields = useCallback(async () => {
    try {
      const { data } = await api.get(`/templatefields/${profile.id_dominio}`)

      console.log(data)

      setContactFields(data)
    } catch (error) {
      console.error('Error fetching contact fields:', error)
    }
  }, [profile.id_dominio])

  useEffect(() => {
    getContactFields()
  }, [getContactFields])

  function updateContactFields(event) {
    setContactFields(
      contactFields.map((item) => {
        if (item.nome_campo === event.target.name) {
          item.conteudo = event.target.value
        }
        return item
      }),
    )
  }

  async function handleAddField() {
    const { value: campo } = await Swal.fire({
      title: 'Adicionar novo campo',
      input: 'text',
      inputPlaceholder: 'Digite o nome do campo',
    })

    if (!campo) {
      return
    }

    if (contactFields.map((item) => item.nome_campo).includes(campo)) {
      await Swal.fire({
        type: 'error',
        title: 'Erro no nome do campo',
        text: 'Já existe um campos com este nome !',
      })
      return
    }

    if (campo && /^[a-z0-9]*$/.test(campo)) {
      setContactFields([
        ...contactFields,
        { nome_campo: campo, conteudo: '', tipo: 1 },
      ])
    } else {
      await Swal.fire({
        type: 'error',
        title: 'Erro no nome do campo',
        text: 'Aceito somente letras minusculas e numeros sem espaços !',
      })
    }
  }

  function handleDeletePhoneNumber(id, nome) {
    const MySwal = withReactContent(Swal)
    MySwal.fire({
      title: `Apagar campo: ${nome}`,
      text: 'Tem certeza que deseja deletar ?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.value) {
        MySwal.fire({
          type: 'success',
          title: 'Campo deletado com sucesso',
        })

        setContactFields(
          contactFields.filter((item) => item.nome_campo !== nome),
        )

        // api.delete(`/contactField/${id}`).then((response) => {
        //   if (response.data.error) {
        //     MySwal.fire({
        //       type: 'error',
        //       title: 'Erro ao deletar campo',
        //       text: 'Para remover um campo de seleção, é necessário remover todas as opções do campo.',
        //     })
        //   } else {
        //     MySwal.fire({
        //       type: 'success',
        //       title: 'Campo deletado com sucesso',
        //     })

        //     setContactFields(
        //       contactFields.filter((item) => item.nome_campo !== nome),
        //     )
        //   }
        // })
      }
      return false
    })
  }

  async function handleSubmit() {
    // const obj = {
    //   did,
    //   descricao,
    //   fraseologia,
    //   ContactFields: contactFields,
    // }
    // const { data } = await api.post('/contact', obj)
    // toast.success(`Contato adicionado com id: ${data.id}`)
    // globalDispatch({
    //   type: 'ADD_CONTACT',
    //   payload: { tickets: [] },
    // })
  }

  const handleAddContactBackDashboard = async () => {
    const obj = {
      did,
      descricao,
      fraseologia,
      ContactFields: contactFields,
      idTicket: search.get('ticket'),
    }

    const { data } = await api.post('/contacts', obj)
    toast.success(`Contato adicionado com id: ${data.id}`)
    globalDispatch({
      type: 'SET_DATA',
      payload: {
        tickets: [],
      },
    })
  }

  return (
    <SemanticContainer>
      <Form onSubmit={handleSubmit}>
        <Message>
          <Form.Input
            name="did"
            fluid
            label="Numero"
            placeholder="Numero"
            value={did || ''}
            onChange={(e) => setDid(e.target.value)}
          />
          <Form.Input
            name="descricao"
            fluid
            label="Nome"
            placeholder="Nome do usuário"
            value={descricao || ''}
            onChange={(e) => setDescricao(e.target.value)}
          />

          <Form.Group className="fields_personalizados">
            {contactFields?.map((field) => {
              if (field.tipo === 1) {
                return (
                  <Form.Input
                    key={field.nome_campo}
                    name={field.nome_campo}
                    fluid
                    label={field.nome_campo}
                    placeholder={field.nome_campo}
                    value={field.conteudo}
                    onChange={(e) => updateContactFields(e)}
                    action={{
                      type: 'button',
                      color: 'red',
                      icon: 'delete',
                      onClick: () => {
                        handleDeletePhoneNumber(field.id, field.nome_campo)
                      },
                    }}
                  />
                )
              }
              if (field.tipo === 2) {
                return (
                  <div className="field field_select">
                    <FormField
                      className="field_select"
                      control={Select}
                      fluid
                      label={field.nome_campo}
                      options={[
                        ...field.template_fields_values.map((item) => {
                          return {
                            key: item.id,
                            text: item.valor,
                            value: item.id,
                          }
                        }),
                      ]}
                      value={field.conteudo}
                      onChange={(e, { value }) => {
                        field.conteudo = value
                        setContactFields([...contactFields])
                      }}
                      placeholder={'campo de seleção'}
                    />
                  </div>
                )
              }

              if (field.tipo === 3) {
                return (
                  <Form.Input
                    key={field.id}
                    name={field.nome_campo}
                    fluid
                    label={field.nome_campo}
                    placeholder={field.nome_campo}
                    value={field.conteudo}
                    onChange={(e, { value }) => {
                      setContactFields(
                        contactFields.map((item) => {
                          if (item.nome_campo === e.target.name) {
                            item.conteudo = e.target.value.replace(/\D/g, '')
                          }
                          return item
                        }),
                      )
                    }}
                    action={{
                      type: 'button',
                      color: 'red',
                      icon: 'delete',
                      onClick: () => {
                        handleDeletePhoneNumber(field.id, field.nome_campo)
                      },
                    }}
                  />
                )
              }

              if (field.tipo === 4) {
                return (
                  <Form.Input
                    key={field.id}
                    error={
                      field.conteudo
                        ? field.conteudo.length > 0 &&
                          !validarCpf(field.conteudo)
                        : true
                    }
                    name={field.nome_campo}
                    fluid
                    label={field.nome_campo}
                    placeholder={field.nome_campo}
                    value={field.conteudo}
                    onChange={(e, { value }) => {
                      setContactFields(
                        contactFields.map((item) => {
                          if (item.nome_campo === e.target.name) {
                            item.conteudo = formatarCpf(e.target.value)
                          }
                          return item
                        }),
                      )
                    }}
                    action={{
                      type: 'button',
                      color: 'red',
                      icon: 'delete',
                      onClick: () => {
                        handleDeletePhoneNumber(field.id, field.nome_campo)
                      },
                    }}
                  />
                )
              }

              if (field.tipo === 5) {
                return (
                  <Form.Input
                    key={field.id}
                    error={
                      field.conteudo
                        ? field.conteudo.length > 0 &&
                          !validarRG(field.conteudo)
                        : true
                    }
                    name={field.nome_campo}
                    fluid
                    label={field.nome_campo}
                    placeholder={field.nome_campo}
                    value={field.conteudo}
                    onChange={(e, { value }) => {
                      setContactFields(
                        contactFields.map((item) => {
                          if (item.nome_campo === e.target.name) {
                            item.conteudo = formatarRg(e.target.value)
                          }
                          return item
                        }),
                      )
                    }}
                    action={{
                      type: 'button',
                      color: 'red',
                      icon: 'delete',
                      onClick: () => {
                        handleDeletePhoneNumber(field.id, field.nome_campo)
                      },
                    }}
                  />
                )
              }

              if (field.tipo === 6) {
                return (
                  <Form.Input
                    key={field.id}
                    error={
                      field.conteudo
                        ? field.conteudo.length > 0 &&
                          !validarCNPJ(field.conteudo)
                        : true
                    }
                    name={field.nome_campo}
                    fluid
                    label={field.nome_campo}
                    placeholder={field.nome_campo}
                    value={field.conteudo}
                    onChange={(e, { value }) => {
                      setContactFields(
                        contactFields.map((item) => {
                          if (item.nome_campo === e.target.name) {
                            item.conteudo = formatarCnpj(e.target.value)
                          }
                          return item
                        }),
                      )
                    }}
                    action={{
                      type: 'button',
                      color: 'red',
                      icon: 'delete',
                      onClick: () => {
                        handleDeletePhoneNumber(field.id, field.nome_campo)
                      },
                    }}
                  />
                )
              }

              if (field.tipo === 7) {
                return (
                  <Form.Input
                    key={field.id}
                    error={
                      field.conteudo
                        ? field.conteudo.length > 0 &&
                          !validarCelular(field.conteudo)
                        : true
                    }
                    name={field.nome_campo}
                    fluid
                    label={field.nome_campo}
                    placeholder={field.nome_campo}
                    value={field.conteudo}
                    onChange={(e, { value }) => {
                      setContactFields(
                        contactFields.map((item) => {
                          if (item.nome_campo === e.target.name) {
                            item.conteudo = formatarCelular(e.target.value)
                          }
                          return item
                        }),
                      )
                    }}
                    action={{
                      type: 'button',
                      color: 'red',
                      icon: 'delete',
                      onClick: () => {
                        handleDeletePhoneNumber(field.id, field.nome_campo)
                      },
                    }}
                  />
                )
              }

              if (field.tipo === 8) {
                return (
                  <Form.Input
                    key={field.id}
                    error={
                      field.conteudo
                        ? field.conteudo.length > 0 &&
                          !validarComRegex(field.conteudo, field.regex)
                        : true
                    }
                    name={field.nome_campo}
                    fluid
                    label={field.nome_campo}
                    placeholder={field.nome_campo}
                    value={field.conteudo}
                    onChange={(e, { value }) => {
                      setContactFields(
                        contactFields.map((item) => {
                          if (item.nome_campo === e.target.name) {
                            item.conteudo = e.target.value
                          }
                          return item
                        }),
                      )
                    }}
                    action={{
                      type: 'button',
                      color: 'red',
                      icon: 'delete',
                      onClick: () => {
                        handleDeletePhoneNumber(field.id, field.nome_campo)
                      },
                    }}
                  />
                )
              }
            })}
          </Form.Group>

          <Button fluid positive type="button" onClick={() => handleAddField()}>
            Adicionar campo
          </Button>
          <Form.TextArea
            name="fraseologia"
            label="Fraseologia"
            placeholder="Fraseologia de atendimento"
            value={fraseologia || ''}
            onChange={(e) => setFraseologia(e.target.value)}
          />

          <Button
            fluid
            primary
            type="button"
            onClick={() => {
              handleAddContactBackDashboard()
            }}
          >
            {/* Adicionar e voltar para o Ticket {search.get('ticket')} */}
            Adicionar
          </Button>
        </Message>
      </Form>
    </SemanticContainer>
  )
}

Contato.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
}
